import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { getProfile } from '@/lib/profile';
import { AUTHENTICATED_LANDING, requireAuth } from '@/lib/route-guards';
import { profileKeys } from '@/features/settings/queries';
import { roleCan, type Access } from './access';

type Ctx = {
  location: { pathname: string; href: string; search: Record<string, unknown> };
  context: { queryClient: QueryClient };
};

/**
 * Whether the caller's role may reach a page needing `access`. `beforeLoad` runs
 * before the loader that primes the profile, so a cold deep link fetches it here.
 * A failed profile read resolves `true` (fall through): the page renders its own
 * error state and the API still enforces access.
 */
export async function isRouteRoleAllowed(queryClient: QueryClient, access: Access): Promise<boolean> {
  const profile = await queryClient
    .ensureQueryData({ queryKey: profileKeys.current(), queryFn: getProfile })
    .catch(() => null);
  return profile == null || roleCan(profile.role, access);
}

/**
 * `beforeLoad` guard: a deep link to a page the caller's role can't use redirects
 * to the app home instead of rendering a lock card or an empty page. Includes
 * `requireAuth`. Role only — plan locks stay in-page (`LockedFeature`).
 */
export function requireRoleAccess(access: Access) {
  return async (ctx: Ctx): Promise<void> => {
    requireAuth(ctx);
    if (!(await isRouteRoleAllowed(ctx.context.queryClient, access))) {
      throw redirect({ to: AUTHENTICATED_LANDING, replace: true });
    }
  };
}
